import * as THREE from 'three';
import { camera, isIsoView } from './Camera';
import {Object3D, WebGLRenderer} from "three";
import { ElementRef } from "@angular/core";
import { changeOutlineColor } from './OutlineShader';
import { changePlaneBorderColor } from "../Shapes/PlaneBorder";
import {
	InfrastructureSchemasInteractionServerService,
	InfrastructureSchemasInteractionService
} from "../../../../../../services/front/infrastructure-schemas-interaction.service";
import {Server} from "../../../../../../services/model/server.model";

interface HoveredObject {
	model: THREE.Object3D;
	outline: THREE.Object3D;
}

let hoveredObjects: HoveredObject[] = [];

const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();
raycaster.layers.set(1);

// Préparer un Layers configuré pour le layer 1
const layer1 = new THREE.Layers();
layer1.set(1);

// Couleurs pour les états normaux et hover
const normalColorPlane = new THREE.Color().setHex(0xB5B5C3);
const normalColor = new THREE.Color(181, 181, 195);
const hoverColor = new THREE.Color(0, 0, 0);

// États globaux pour les interactions
let isInteractionDragging = false;
let isCameraDragging = false;

// Fonction pour calculer les coordonnées normalisées correctes
function calculateNormalizedCoordinates(event: MouseEvent, container: HTMLElement, renderer: WebGLRenderer): THREE.Vector2 {
	// Récupérer les dimensions du conteneur et du canvas
	const containerRect = container.getBoundingClientRect();
	const canvasRect = renderer.domElement.getBoundingClientRect();

	// Coordonnées relatives au conteneur
	const mouseX = event.clientX - containerRect.left;
	const mouseY = event.clientY - containerRect.top;

	// Coordonnées normalisées par rapport au conteneur (-1 à 1)
	const normalizedX = (mouseX / containerRect.width) * 2 - 1;
	const normalizedY = -((mouseY / containerRect.height) * 2 - 1);

	// Ajustement pour la caméra orthographique
	// Pour une caméra orthographique, nous devons ajuster en fonction de la frustum
	const frustumHeight = Math.abs(camera.top - camera.bottom);
	const frustumWidth = Math.abs(camera.right - camera.left);
	const aspectContainer = containerRect.width / containerRect.height;

	// Création des coordonnées ajustées
	const adjustedCoords = new THREE.Vector2();

	// Si le conteneur est plus large que haut, ajuster X
	// Si le conteneur est plus haut que large, ajuster Y
	if (aspectContainer > frustumWidth / frustumHeight) {
		adjustedCoords.x = normalizedX * (aspectContainer / (frustumWidth / frustumHeight));
		adjustedCoords.y = normalizedY;
	} else {
		adjustedCoords.x = normalizedX;
		adjustedCoords.y = normalizedY * ((frustumWidth / frustumHeight) / aspectContainer);
	}

	return adjustedCoords;
}

function onMouseMove(event: MouseEvent, container: HTMLElement, cameraControls?: InteractionsAndControls, scene?: THREE.Scene, renderer?: WebGLRenderer): void {
	if(!scene){return;}
	if(!renderer){return;}

	// Gérer le déplacement de la caméra si nous sommes en train de faire un glisser-déposer
	if (isCameraDragging && cameraControls) {
		cameraControls.handleMouseMove(event, scene);
		return;
	}

	// Calculer les coordonnées normalisées correctes
	const adjustedCoords = calculateNormalizedCoordinates(event, container, renderer);
	mouse.copy(adjustedCoords);

	// Utiliser le raycaster avec les coordonnées ajustées
	raycaster.setFromCamera(mouse, camera);
	const intersections = raycaster.intersectObjects(scene.children, true);

	// Trouver tous les modèles survolés (pas les outlines)
	const newHoveredModels = intersections
		.map(intersect => intersect.object)
		.filter(obj => obj.layers.test(layer1) && !obj.userData.isOutline)
		.map(obj => {
			// Remonter jusqu'à l'objet parent qui contient l'ID
			let currentObj: Object3D | null = obj;
			while (currentObj && !currentObj.userData.id) {
				currentObj = currentObj.parent;
			}
			return currentObj;
		})
		.filter(Boolean); // Éliminer les objets undefined

	// Changement de pointeur
	if (intersections.length > 0) {
		container.style.cursor = 'pointer';
	} else {
		container.style.cursor = 'grab';
	}

	// Créer une nouvelle liste d'objets survolés avec leurs outlines
	const newHoveredObjects: HoveredObject[] = [];

	newHoveredModels.forEach(model => {
		// Éviter les doublons
		if (model && !newHoveredObjects.some(item => item.model === model)) {
			// Trouver l'outline correspondant
			let outline = null;

			// Si le modèle a une référence directe à son outline
			if (model.userData.outlineRef) {
				outline = model.userData.outlineRef;
			}
			// Sinon, chercher parmi les enfants du parent
			else if (model.parent) {
				model.parent.children.forEach(child => {
					if (child.userData.isOutline &&
						(child.userData.targetModelId === model.userData.id ||
							!child.userData.targetModelId)) {
						outline = child;
					}
				});
			}

			newHoveredObjects.push({ model, outline });
		}
	});

	// Appliquer l'effet de hover sur les nouveaux objets survolés
	newHoveredObjects.forEach(obj => {
		// Gérer le soulignement pour les textes de réseau
		if (obj.model.userData.modelType === "Network") {
			// Chercher le soulignement en parcourant le groupe ou les enfants
			if (obj.model.type === 'Group') {
				// Si le modèle est un groupe, chercher l'underline parmi ses enfants directs
				obj.model.children.forEach(child => {
					if (child.name && child.name.startsWith('underline-')) {
						child.visible = true;
					}
				});
			} else if (obj.model.userData.underLineRef) {
				// Si le modèle a une référence directe à son underline
				obj.model.userData.underLineRef.visible = true;
			} else if (obj.model.userData.underlineName) {
				// Si le modèle a un nom d'underline, rechercher dans la scène
				scene.traverse(child => {
					if (child.name === obj.model.userData.underlineName) {
						child.visible = true;
					}
				});
			}
		}

		// Appliquer l'effet standard d'outline seulement si l'outline existe
		if (obj.outline) {
			applyHoverEffect(obj.outline, true);
		}
	});

	// Restaurer l'état normal pour les objets qui ne sont plus survolés
	hoveredObjects.forEach(obj => {
		// Vérifier si l'objet n'est plus survolé
		if (!newHoveredObjects.some(newObj => newObj.model === obj.model)) {
			// Masquer le soulignement pour les textes de réseau
			if (obj.model.userData.modelType === "Network") {
				if (obj.model.type === 'Group') {
					// Si le modèle est un groupe, chercher l'underline parmi ses enfants
					obj.model.children.forEach(child => {
						if (child.name && child.name.startsWith('underline-')) {
							child.visible = false;
						}
					});
				} else if (obj.model.userData.underLineRef) {
					// Si le modèle a une référence directe à son underline
					obj.model.userData.underLineRef.visible = false;
				} else if (obj.model.userData.underlineName) {
					// Si le modèle a un nom d'underline, rechercher dans la scène
					scene.traverse(child => {
						if (child.name === obj.model.userData.underlineName) {
							child.visible = false;
						}
					});
				}
			}

			// Restaurer l'état normal de l'outline seulement si l'outline existe
			if (obj.outline) {
				applyHoverEffect(obj.outline, false);
			}
		}
	});

	// Mettre à jour la liste des objets survolés
	hoveredObjects = newHoveredObjects;
}

// Fonction unifiée pour appliquer l'effet de hover en fonction du type d'outline
function applyHoverEffect(outline: THREE.Object3D, isHovered: boolean): void {
	if (!outline) return;

	const color = isHovered ? hoverColor : normalColor;
	const colorPlane = isHovered ? hoverColor : normalColorPlane;

	// Appliquer les effets de couleur selon le type
	if (outline.userData.outlineMaterial) {
		// C'est un PlaneBorder
		changePlaneBorderColor(outline, colorPlane);
	} else {
		// C'est un OutlineShader
		changeOutlineColor(outline, color);
	}
}

function onMouseDown(
	event: MouseEvent,
	container: HTMLElement,
	infrastructureSchemasInteractionService: InfrastructureSchemasInteractionService,
	infrastructureSchemasInteractionServerService: InfrastructureSchemasInteractionServerService,
	cameraControls?: InteractionsAndControls,
	scene?: THREE.Scene,
	renderer?: THREE.WebGLRenderer
): void {
	if (!scene){return}
	if (!renderer){return}
	// Utiliser la même fonction de calcul des coordonnées que pour onMouseMove
	const adjustedCoords = calculateNormalizedCoordinates(event, container, renderer);
	mouse.copy(adjustedCoords);

	// Déterminer si nous allons interagir avec un objet ou déplacer la caméra
	let interactWithObject = false;

	// Utiliser le raycaster avec les coordonnées ajustées
	raycaster.setFromCamera(mouse, camera);
	const intersections = raycaster.intersectObjects(scene.children, true);

	if (intersections.length > 0) {
		let selectedObject: THREE.Object3D | null = intersections[0].object;
		let modelId: string | null = null;
		let modelType: string | null = null;
		let servers: Server[] | null = null;

		// Recherche de l'ID en remontant dans la hiérarchie des objets
		while (selectedObject) {
			// Vérifier si l'objet actuel a un ID
			if (selectedObject.userData && selectedObject.userData.id) {
				modelId = selectedObject.userData.id;
				modelType = selectedObject.userData.modelType;
				break;
			}

			if (selectedObject.userData && selectedObject.userData.data) {
				servers = selectedObject.userData.data;
				break;
			}

			// Si l'objet actuel n'a pas d'ID, remonter au parent
			selectedObject = selectedObject.parent;
		}

		// Si un ID a été trouvé, l'afficher dans la console et indiquer qu'on interagit avec un objet
		if (modelId && modelType) {
			infrastructureSchemasInteractionService.click(modelId, modelType);
			interactWithObject = true;
			isInteractionDragging = true;
		}

		// Pour le bouton plus
		if (servers) {
			infrastructureSchemasInteractionServerService.click(servers);
			interactWithObject = true;
			isInteractionDragging = true;
		}
	}

	// Si nous n'interagissons pas avec un objet, commencer à déplacer la caméra
	if (!interactWithObject && cameraControls) {
		container.style.cursor = 'grabbing';
		isCameraDragging = true;
		cameraControls.handleMouseDown(event);
	}
}

function onMouseUp(event: MouseEvent, container: HTMLElement, cameraControls?: InteractionsAndControls): void {
	container.style.cursor = 'grab';
	// Réinitialiser les états de glisser-déposer
	isInteractionDragging = false;
	isCameraDragging = false;

	if (cameraControls) {
		cameraControls.handleMouseUp();
	}
}

function onWheel(event: WheelEvent, container: HTMLElement, cameraControls?: InteractionsAndControls, scene?: THREE.Scene): void {
	if(!scene){return}
	if (cameraControls) {
		cameraControls.handleWheel(event, scene);
	}
}

export class InteractionsAndControls {
	private isDragging: boolean = false;
	private previousMousePosition: { x: number; y: number } = { x: 0, y: 0 };
	private initialPinchDistance: number = 0;
	private initialTwoFingerPosition: { x: number; y: number } = {x: 0, y: 0};

	// Limites de la caméra
	cameraBounds: {
		minX: number;
		maxX: number;
		minZ: number;
		maxZ: number;
	}

	// Événements de scrollbar
	public onCameraMove: (xPercent: number, zPercent: number) => void = () => {
	};

	constructor(private container: HTMLElement) {
	}

	// Utiliser les bornes calculées par CalculateTotalSize
	public setBoundsFromCalculatedSize(sizeMax: { xMin: number, xMax: number, zMin: number, zMax: number }, scene: THREE.Scene): void {
		const margin = 20; // Marge pour éviter que les objets ne soient trop près des bords

		this.cameraBounds = {
			minX: sizeMax.xMin - margin,
			maxX: sizeMax.xMax + margin,
			minZ: sizeMax.zMin - margin,
			maxZ: sizeMax.zMax + margin
		};

		this.enforceCameraBounds();
		this.updateScrollbarPosition();

		// Centrer la caméra initialement
		this.recenter(scene);
	}

	public handleMouseDown(event: MouseEvent): void {
		this.isDragging = true;
		this.previousMousePosition.x = event.clientX;
		this.previousMousePosition.y = event.clientY;
	}

	public handleMouseMove(event: MouseEvent, scene: THREE.Scene): void {
		if (!this.isDragging) return;

		this.container.style.cursor = 'grabbing';

		const deltaX: number = event.clientX - this.previousMousePosition.x;
		const deltaY: number = event.clientY - this.previousMousePosition.y;

		this.moveCameraByDelta(deltaX, deltaY, scene);

		this.previousMousePosition.x = event.clientX;
		this.previousMousePosition.y = event.clientY;
	}

	public handleMouseUp(): void {
		this.isDragging = false;
	}

	public handleWheel(event: WheelEvent, scene: THREE.Scene): void {
		if (event.ctrlKey || event.metaKey) {
			const zoomFactor: number = 1.02;
			this.zoomCamera(event.deltaY < 0 ? zoomFactor : 1 / zoomFactor, scene);
		} else {
			this.container.style.cursor = 'grab';
			this.moveCameraByDelta(-event.deltaX, -event.deltaY, scene);
		}
	}

	private moveCameraByDelta(deltaX: number, deltaY: number, scene: THREE.Scene): void {
		const worldUnitsPerPixel: number = ((camera.right - camera.left) / this.container.clientWidth) / camera.zoom;

		const oldX = camera.position.x;
		const oldZ = camera.position.z;

		if (isIsoView) {
			const angle: number = Math.PI / 4;
			const cosA: number = Math.cos(angle);
			const sinA: number = Math.sin(angle);
			camera.position.x -= (deltaX * cosA + deltaY * sinA) * worldUnitsPerPixel;
			camera.position.z -= (-deltaX * sinA + deltaY * cosA) * worldUnitsPerPixel;
		} else {
			camera.position.x -= deltaX * worldUnitsPerPixel;
			camera.position.z -= deltaY * worldUnitsPerPixel;
		}

		// Appliquer les limites à la caméra
		this.enforceCameraBounds();

		// Si la position a changé, mettre à jour les éléments et les scrollbars
		if (oldX !== camera.position.x || oldZ !== camera.position.z) {
			this.updateSceneElements(scene);
			this.updateScrollbarPosition();
		}
	}

	private zoomCamera(zoomMultiplier: number, scene: THREE.Scene): void {
		const oldZoom = camera.zoom;

		camera.zoom *= zoomMultiplier;
		camera.zoom = Math.max(0.5, Math.min(2, camera.zoom));
		camera.updateProjectionMatrix();

		// Si le zoom a changé, mettre à jour les éléments et les scrollbars
		if (oldZoom !== camera.zoom) {
			this.updateSceneElements(scene);
			this.updateScrollbarPosition();
		}
	}

	private enforceCameraBounds(): void {
		const visibleWidth = (camera.right - camera.left) / camera.zoom;
		const visibleHeight = (camera.top - camera.bottom) / camera.zoom;

		// Empêcher la caméra de sortir des limites
		camera.position.x = Math.max(
			this.cameraBounds.minX + visibleWidth / 2,
			Math.min(this.cameraBounds.maxX - visibleWidth / 2, camera.position.x)
		);

		camera.position.z = Math.max(
			this.cameraBounds.minZ + visibleHeight / 2,
			Math.min(this.cameraBounds.maxZ - visibleHeight / 2, camera.position.z)
		);
	}

	// Méthode pour mettre à jour la position des scrollbars
	private updateScrollbarPosition(): void {
		// Calculer la position en pourcentage de la caméra par rapport aux limites
		const totalX = this.cameraBounds.maxX - this.cameraBounds.minX;
		const totalZ = this.cameraBounds.maxZ - this.cameraBounds.minZ;

		// Ajuster pour tenir compte de la zone visible
		const visibleWidth = (camera.right - camera.left) / camera.zoom;
		const visibleHeight = (camera.top - camera.bottom) / camera.zoom;

		// Calculer le pourcentage de la scène qui est visible
		const visibleWidthPercent = Math.min(1, visibleWidth / totalX);
		const visibleHeightPercent = Math.min(1, visibleHeight / totalZ);

		// Si la totalité de la scène est visible, masquer les scrollbars en mettant le pourcentage à une valeur spéciale
		if (visibleWidthPercent >= 1 && visibleHeightPercent >= 1) {
			if (this.onCameraMove) {
				// Une valeur négative indiquera au composant de masquer les scrollbars
				this.onCameraMove(-1, -1);
			}
			return;
		}

		// Calculer la position relative de la caméra en tenant compte de la taille du thumb
		// La taille du thumb représente la partie visible par rapport au total
		const thumbSizeX = visibleWidthPercent;
		const thumbSizeZ = visibleHeightPercent;

		// Nous devons ajuster la plage pour que le thumb ne sorte jamais de la track
		// La plage valide est de 0 à (1 - taille du thumb)
		const maxRangeX = Math.max(0, 1 - thumbSizeX);
		const maxRangeZ = Math.max(0, 1 - thumbSizeZ);

		// Calculer où se trouve la caméra par rapport à la plage totale
		let rawXPercent = totalX <= visibleWidth ? 0 :
			(camera.position.x - this.cameraBounds.minX - visibleWidth / 2) / (totalX - visibleWidth);

		let rawZPercent = totalZ <= visibleHeight ? 0 :
			(camera.position.z - this.cameraBounds.minZ - visibleHeight / 2) / (totalZ - visibleHeight);

		// Maintenant, ajuster ces pourcentages à la plage valide
		const xPercent = maxRangeX > 0 ? (rawXPercent * maxRangeX) : 0;
		const zPercent = maxRangeZ > 0 ? (rawZPercent * maxRangeZ) : 0;

		// Limiter aux valeurs entre 0 et leur maximum respectif
		const clampedXPercent = Math.max(0, Math.min(maxRangeX, xPercent));
		const clampedZPercent = Math.max(0, Math.min(maxRangeZ, zPercent));

		// Déclencher l'événement de mise à jour des scrollbars
		if (this.onCameraMove) {
			this.onCameraMove(clampedXPercent, clampedZPercent);
		}
	}

	// Méthode pour déplacer la caméra en fonction de la position des scrollbars
	public moveCameraToScrollPosition(xPercent: number, zPercent: number, scene: THREE.Scene): void {
		// Calculer la zone visible par la caméra en fonction du zoom
		const visibleWidth = (camera.right - camera.left) / camera.zoom;
		const visibleHeight = (camera.top - camera.bottom) / camera.zoom;

		// Calculer le pourcentage de la scène qui est visible
		const totalX = this.cameraBounds.maxX - this.cameraBounds.minX;
		const totalZ = this.cameraBounds.maxZ - this.cameraBounds.minZ;

		const visibleWidthPercent = Math.min(1, visibleWidth / totalX);
		const visibleHeightPercent = Math.min(1, visibleHeight / totalZ);

		// Calculer la plage maximale pour le scroll
		const maxRangeX = Math.max(0, 1 - visibleWidthPercent);
		const maxRangeZ = Math.max(0, 1 - visibleHeightPercent);

		// Convertir les pourcentages de la scrollbar en pourcentages pour la caméra
		// (inverser le calcul fait dans updateScrollbarPosition)
		const cameraXPercent = maxRangeX > 0 ? (xPercent / maxRangeX) : 0;
		const cameraZPercent = maxRangeZ > 0 ? (zPercent / maxRangeZ) : 0;

		// Calculer les nouvelles positions
		const newX = this.cameraBounds.minX + visibleWidth / 2 + cameraXPercent * (totalX - visibleWidth);
		const newZ = this.cameraBounds.minZ + visibleHeight / 2 + cameraZPercent * (totalZ - visibleHeight);

		// Appliquer les nouvelles positions
		camera.position.x = newX;
		camera.position.z = newZ;

		this.updateSceneElements(scene);
	}

	private updateSceneElements(scene: THREE.Scene): void {
		scene.traverse((child: Object3D): void => {
			if (child.userData.followCameraX) {
				const margin = 1;
				child.position.x = camera.position.x + (camera.left / camera.zoom) + margin;

				const scaleFactor = camera.zoom > 1 ? 1 : 1 / camera.zoom;
				child.scale.set(scaleFactor, scaleFactor, scaleFactor);
			}
		});
	}

	// Méthodes publiques pour le contrôle externe
	public recenter(scene: THREE.Scene): void {
		if (isIsoView) {
			camera.position.set(30, 30, 30);
			camera.lookAt(0, 0, 0);
			camera.up.set(0, 0, -1); // X horizontal, Z vertical
		} else {
			camera.position.set(0, 30, 0);
			camera.lookAt(0, 0, 0);
			camera.up.set(0, 1, 0); // X horizontal, Z vertical
		}

		this.updateSceneElements(scene);
		this.updateScrollbarPosition();
	}

	public zoomIn(): void {
		if (camera.zoom < 2) {
			if (camera.zoom <= 1) {
				camera.zoom += 0.1;
			} else {
				camera.zoom += 0.2;
			}
		}
		camera.updateProjectionMatrix();
	}

	public zoomOut(): void {
		if (camera.zoom > 0.5) {
			if (camera.zoom <= 1) {
				camera.zoom -= 0.1;
			} else {
				camera.zoom -= 0.2;
			}
		}
		camera.updateProjectionMatrix();
	}

	public getZoomPercentage(): number {
		const zoom = camera.zoom;
		if (zoom <= 1) {
			return Math.max(5 + (zoom - 0.5) * (95 / 0.5), 5); // de 5% à 100%
		} else {
			return 100 + (zoom - 1) * (100); // de 100% à 200%
		}
	}

	public dispose(): void {
		// Réinitialiser les états
		this.isDragging = false;

		// Réinitialiser les callbacks
		this.onCameraMove = () => {};
	}
}

export function setupInteractions(
	elt: ElementRef,
	infrastructureSchemaInteractionService: InfrastructureSchemasInteractionService,
	infrastructureSchemasInteractionServerService: InfrastructureSchemasInteractionServerService,
	scene: THREE.Scene,
	renderer: THREE.WebGLRenderer
): {
	cameraControls: InteractionsAndControls;
	disposeInteractions: () => void;
} {
	const container = elt.nativeElement;
	const cameraControls = new InteractionsAndControls(container);

	// Stocker les références aux fonctions de gestionnaire d'événements
	const mouseMoveHandler = (event: MouseEvent) =>
		onMouseMove(event, container, cameraControls, scene, renderer);

	const mouseDownHandler = (event: MouseEvent) =>
		onMouseDown(event, container, infrastructureSchemaInteractionService, infrastructureSchemasInteractionServerService, cameraControls, scene, renderer);

	const mouseUpHandler = (event: MouseEvent) =>
		onMouseUp(event, container, cameraControls);

	const wheelHandler = (event: WheelEvent) =>
		onWheel(event, container, cameraControls, scene);

	const documentMouseUpHandler = () => {
		isInteractionDragging = false;
		isCameraDragging = false;
		cameraControls.handleMouseUp();
	};

	// Configuration des écouteurs d'événements
	container.addEventListener('mousemove', mouseMoveHandler);
	container.addEventListener('mousedown', mouseDownHandler);
	container.addEventListener('mouseup', mouseUpHandler);
	container.addEventListener('mouseleave', mouseUpHandler);
	container.addEventListener('wheel', wheelHandler);
	document.addEventListener('mouseup', documentMouseUpHandler);

	// Fonction pour supprimer tous les écouteurs d'événements
	const disposeInteractions = () => {
		// Supprimer les écouteurs du conteneur
		container.removeEventListener('mousemove', mouseMoveHandler);
		container.removeEventListener('mousedown', mouseDownHandler);
		container.removeEventListener('mouseup', mouseUpHandler);
		container.removeEventListener('mouseleave', mouseUpHandler);
		container.removeEventListener('wheel', wheelHandler);

		// Supprimer l'écouteur global
		document.removeEventListener('mouseup', documentMouseUpHandler);

		// Réinitialiser les états globaux
		isInteractionDragging = false;
		isCameraDragging = false;

		// Réinitialiser la liste des objets survolés
		hoveredObjects = [];

		// Appeler la méthode dispose du contrôleur de caméra
		cameraControls.dispose();
	};

	return { cameraControls, disposeInteractions };
}
